import type { LinkedToOtherPayments, PaymentDiscrepancy } from '../components/accounting/types';
import axios from 'axios';
import { SyncStatus, B2BProduct, LocalProduct } from '../types/orders';

export interface SyncStatusResponse {
  success: boolean;
  syncStatus: {
    orders?: SyncStatus;
    products?: SyncStatus;
  };
}

/** What the order sync did with the fetched orders. Each order is in exactly one count. */
export interface SyncOrdersCounts {
  newDevice: number;
  newSoftware: number;
  updated: number;
  deleted: number;
  skipped: number;
  errors: number;
}

export interface SyncOrdersResponse {
  success: boolean;
  synced: number;
  eligible: number;
  total?: number;
  counts?: SyncOrdersCounts;
  pageCapped?: boolean;
  /**
   * Summary of linking payments to the synced orders. null when the linking
   * failed as a whole; errors lists the parts that failed.
   */
  backfill?: {
    errors: string[] | null;
    linkedToOtherPayments?: LinkedToOtherPayments[];
    bankTransferDiscrepancies?: PaymentDiscrepancy[];
  } | null;
  message?: string;
}

export interface SyncProductsResponse {
  success: boolean;
  synced: number;
  unmapped: number;
  message?: string;
}

export interface ProductsResponse {
  success: boolean;
  products: B2BProduct[];
}

export interface LocalProductsResponse {
  success: boolean;
  products: LocalProduct[];
}

export interface UpdateProductMappingRequest {
  local_product_id: number | null;
  is_ignored: boolean;
}

export interface UpdateProductMappingResponse {
  success: boolean;
  message?: string;
}

/**
 * Fetch sync status for orders and products
 */
export const fetchSyncStatus = async (): Promise<SyncStatusResponse> => {
  const { data } = await axios.get('/api/b2b/sync-status');
  return data;
};

/**
 * Sync orders from B2B
 */
export const syncOrders = async (): Promise<SyncOrdersResponse> => {
  const { data } = await axios.post('/api/b2b/sync-orders');
  return data;
};

export const syncOrdersDateRange = async (after: string, before: string): Promise<SyncOrdersResponse> => {
  const { data } = await axios.post('/api/b2b/sync-orders', { after, before });
  return data;
};

/**
 * Sync products from B2B
 */
export const syncProducts = async (): Promise<SyncProductsResponse> => {
  const { data } = await axios.post('/api/b2b/sync-products');
  return data;
};

/**
 * Fetch B2B products with optional filter
 */
export const fetchB2BProducts = async (
  filter: 'all' | 'unmapped' | 'has_serial' = 'all'
): Promise<ProductsResponse> => {
  const { data } = await axios.get(`/api/b2b/products?filter=${filter}`);
  return data;
};

/**
 * Fetch local products
 */
export const fetchLocalProducts = async (): Promise<LocalProductsResponse> => {
  const { data } = await axios.get('/api/b2b/products/local');
  return data;
};

/**
 * Update product mapping
 */
export const updateProductMapping = async (
  productId: number,
  mapping: UpdateProductMappingRequest
): Promise<UpdateProductMappingResponse> => {
  const { data } = await axios.put(`/api/b2b/products/${productId}/mapping`, mapping);
  return data;
};

/**
 * Delete a B2B product
 */
export const deleteProduct = async (
  productId: number
): Promise<UpdateProductMappingResponse> => {
  const { data } = await axios.delete(`/api/b2b/products/${productId}`);
  return data;
};

export interface SyncSingleOrderResponse {
  success: boolean;
  error?: string;
  order?: {
    id: number;
    woo_order_id: number;
    order_number: string;
    customer_name: string;
    status: string;
    items_synced: number;
  };
  line_items?: Array<{ product_id: number; name: string; mapped: boolean }>;
}

/**
 * Sync a single order by its WooCommerce order number/ID
 */
export const syncSingleOrder = async (orderNumber: string): Promise<SyncSingleOrderResponse> => {
  const { data } = await axios.post('/api/b2b/sync-single-order', { orderNumber });
  return data;
};

export interface OrderStatusesResponse {
  success: boolean;
  statuses: Array<{
    key: string;
    label: string;
    description: string;
  }>;
  mapping: Record<string, string>;
}

/**
 * Fetch order statuses from WooCommerce
 */
export const fetchOrderStatuses = async (): Promise<OrderStatusesResponse> => {
  const { data } = await axios.get('/api/b2b/order-statuses');
  return data;
};

